/**
 * File: TodayPage.tsx
 * Path: apps/admin/src/routes/today/TodayPage.tsx
 * Description: The CRM home. Same three sections as the accounting Overview:
 * the four figures that matter; money in beside one "Needs attention" list
 * (follow-ups, overdue payments, unpaid deposits, stale deals, care plans
 * ending), the invoices Ready to send and the Reminders to send; then the pipeline, the quotes and the clients that pay for your
 * time. The graph sets the height of the second row and the list scrolls on
 * its own inside its box. The date range drives the graph and the bottom
 * row; the four figures are as of today. The one main button is "New".
 */

import React from "react";
import { ChevronDown, Plus } from "lucide-react";
import { DEFAULT_YEAR_END_MONTH } from "@idk/config";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@idk/ui";
import { cn } from "@idk/utils";
import { PageHeader } from "@/modules/layout/components/PageHeader";
import { StatStrip } from "@/modules/layout/components/StatStrip";
import { Box, Figure, SectionLabel, notice } from "@/shared/components/BooksPrimitive";
import { AttentionList } from "@/shared/components/overview/AttentionList";
import { BarList } from "@/shared/components/overview/BarList";
import { CashFlowChart } from "@/shared/components/overview/CashFlowChart";
import { DateRangeSelect } from "@/shared/components/overview/DateRangeSelect";
import { AddLeadDialog } from "@/shared/components/AddLeadDialog";
import { ReadyToSendCard } from "@/shared/components/ReadyToSendCard";
import { RemindersCard } from "@/features/today/components/RemindersCard";
import { useNewMenuController } from "@/features/today/hooks/useNewMenuController";
import { useTodayBodyController } from "@/features/today/hooks/useTodayBodyController";
import { useTodayPageController } from "@/features/today/hooks/useTodayPageController";
import { figureChips } from "@/features/today/utils/todayFiguresUtils";
import { BOOKS_START, hoursText, type DayRange, type TodayOverview } from "@/features/today/utils/todayOverviewUtils";

const empty = "border border-dashed border-border/60 p-gutter-md text-sm text-muted-foreground";

const TodayFigures: React.FC<{ overview: TodayOverview; debtors: { cents: number; lateCount: number } | null }> = ({ overview, debtors }) => {
  const f = overview.figures;
  const chips = figureChips(f, debtors);
  return (
    <StatStrip as="dl" variant="cards" label="Key figures" desktop="sm:grid-cols-2 lg:grid-cols-4">
      <Figure
        label="Owed to you"
        cents={debtors ? debtors.cents : f.owedCents}
        explain={debtors ? "Invoices clients haven't paid yet." : "Contract payments clients haven't paid yet."}
        chip={chips.owed}
      />
      <Figure
        label="Monthly recurring"
        cents={f.mrrCents}
        explain="Care plan fees that come in every month."
        chip={chips.mrr}
      />
      <Figure
        label="Open pipeline"
        cents={f.openValueCents}
        explain="Deals still open, added up."
        chip={chips.pipeline}
      />
      <Figure
        label="Hours this week"
        value={hoursText(f.weekMinutes)}
        explain="Time logged on client work since Monday."
        chip={chips.hours}
      />
    </StatStrip>
  );
};

const TodayBody: React.FC<{ range: DayRange; today: string }> = ({ range, today }) => {
  const { failed, overview, debtors, grain, setGrain } = useTodayBodyController(range, today);

  if (failed) {
    return (
      <div role="alert" className={notice}>
        <p className="font-bold text-foreground">Today could not be loaded.</p>
        <p className="text-sm text-muted-foreground">Open the page again in a moment.</p>
      </div>
    );
  }
  if (!overview) return <p role="status" className="text-sm text-muted-foreground">Adding everything up…</p>;

  return (
    <div className="space-y-gutter-lg">
      <p className="text-sm text-muted-foreground">
        IDK Solutions · what needs you today, and how the work is going. The graph and the boxes at the bottom follow the dates above.
      </p>

      <section className="space-y-stack-sm" aria-labelledby="today-figures">
        <SectionLabel>
          <span id="today-figures">Where things stand</span>
        </SectionLabel>
        <TodayFigures overview={overview} debtors={debtors} />
      </section>

      <section className="space-y-stack-sm" aria-labelledby="today-money">
        <SectionLabel>
          <span id="today-money">Money in and what to do</span>
        </SectionLabel>
        <ReadyToSendCard />
        <RemindersCard />
        <div className="grid grid-cols-1 gap-stack-md md:grid-cols-3">
          <Box
            title="Money in from clients"
            caption="What clients paid you each month, and how much of it was care plan fees. The scale fits your figures."
            className="md:col-span-2"
          >
            <div className="flex" role="group" aria-label="Group the chart by">
              {(["week", "month"] as const).map((g) => (
                <button
                  key={g}
                  type="button"
                  aria-pressed={grain === g}
                  onClick={() => setGrain(g)}
                  className={cn(
                    "min-h-11 border border-border px-gutter-md text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    grain === g ? "bg-primary text-primary-foreground" : "bg-card text-foreground hover:bg-secondary",
                  )}
                >
                  {g === "week" ? "Weekly" : "Monthly"}
                </button>
              ))}
            </div>
            <CashFlowChart
              points={overview.flow}
              className="flex-1"
              names={{ in: "All money in", out: "Of which care plans", legendIn: "All money in", legendOut: "Of which care plans" }}
            />
          </Box>
          <Box title="Needs attention" caption="Follow-ups, money to chase, unpaid deposits and care plans ending, most urgent first. Open one to act.">
            <AttentionList
              fill
              items={overview.attention.map((a) => ({ ...a, to: a.href }))}
              label="Needs attention"
              empty="Nothing needs you right now."
            />
          </Box>
        </div>
      </section>

      <section className="space-y-stack-sm" aria-labelledby="today-work">
        <SectionLabel>
          <span id="today-work">Pipeline, quotes and clients</span>
        </SectionLabel>
        <div className="grid grid-cols-1 gap-stack-md md:grid-cols-3">
          <Box title="Open pipeline by stage" caption="Where your open deals sit, and what they are worth.">
            <BarList items={overview.pipeline} label="Open pipeline by stage" />
          </Box>
          <Box title="Quotes in these dates" caption="How many quotes became work, and what winning a client costs.">
            <BarList items={overview.quotes} label="Quotes in these dates" />
          </Box>
          <Box title="Margin and hours per client" caption="Which clients pay for your time. Margin is what they paid less your time and costs.">
            {overview.clients.length === 0 ? (
              <p className={empty}>
                No client income or time in these dates. Log time from a client's page to see it here.
              </p>
            ) : (
              <BarList items={overview.clients} label="Margin and hours per client" />
            )}
          </Box>
        </div>
      </section>
    </div>
  );
};

/** The one main button: start something new. */
const NewMenu: React.FC<{ onNewLead: () => void }> = ({ onNewLead }) => {
  const { items } = useNewMenuController(onNewLead);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" className="h-11 w-full rounded-none sm:w-auto">
          <Plus className="mr-stack-xs h-4 w-4" aria-hidden />
          New
          <ChevronDown className="ml-stack-xs h-4 w-4" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 rounded-none border-border bg-card">
        {items.map((item) => (
          <DropdownMenuItem key={item.label} className="min-h-11 flex-col items-start gap-stack-xs" onSelect={item.run}>
            <span className="font-bold">{item.label}</span>
            <span className="text-xs text-muted-foreground">{item.hint}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const TodayPage: React.FC = () => {
  const { today, range, presets, setRange, adding, setAdding, openAddLead } = useTodayPageController();

  return (
    <>
      <PageHeader title="Today">
        <DateRangeSelect
          range={range}
          today={today}
          booksStart={BOOKS_START}
          yearEndMonth={DEFAULT_YEAR_END_MONTH}
          presetRange={presets}
          onChange={setRange}
        />
        <NewMenu onNewLead={openAddLead} />
      </PageHeader>
      {/* Keyed on the dates so a new range loads its own time and costs. */}
      <TodayBody key={`${range.from}:${range.to}`} range={range} today={today} />
      <AddLeadDialog open={adding} onOpenChange={setAdding} />
    </>
  );
};

